import { useLocation, useRouter } from '@tanstack/react-router';
import { useGetOrganizationsColorMap } from '@vizit/api';
import { APIError, APIErrorType, useAuth } from '@vizit/auth';
import { LoginError } from '@vizit/organisms';
import * as React from 'react';
import { useAnalytics } from '../../../analytics';

function usePagesProvider() {
  const router = useRouter();
  const location = useLocation();

  const {
    userEmail,
    userId,
    organizationName,
    isVizitUser,
    isDisplayingBanner,
    organizationId,
    defaultPortfolioId,
    onRemoveAuthentication,
    onDismissBanner,
  } = useAuth();
  const { onClearSession } = useAnalytics();
  const { data: organizationsColors } = useGetOrganizationsColorMap(
    {
      user_id: userId ?? '',
    },
    {
      enabled: !!userId && !!organizationId,
    },
  );

  const avatarColor = React.useMemo(() => {
    return localStorage.getItem('avatar_index') ? Number.parseInt(localStorage.getItem('avatar_index') ?? '0') : 0;
  }, []);

  const organizationBadgeColor = React.useMemo(() => {
    return organizationsColors && organizationId ? organizationsColors[organizationId]?.color : undefined;
  }, [organizationsColors, organizationId]);
  const organizationBadgeTextColor = React.useMemo(() => {
    return organizationsColors && organizationId ? organizationsColors[organizationId]?.textColor : undefined;
  }, [organizationsColors, organizationId]);

  const handleOnAuthenticationError = React.useCallback(
    async (error: unknown | APIError) => {
      if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
        await onRemoveAuthentication();

        onClearSession();

        router.navigate({
          to: '/login',
          search: {
            error: LoginError.SessionExpired,
            redirect: location.href && location.href !== '/' ? location.href : undefined,
          },
        });
      }
    },
    [onRemoveAuthentication, onClearSession, router, location.href],
  );

  return {
    avatarColor,
    isVizitUser,
    isDisplayingBanner,
    organizationId,
    organizationName,
    organizationBadgeColor,
    organizationBadgeTextColor,
    portfolioId: defaultPortfolioId,
    userEmail,
    userId,
    onAuthenticationError: handleOnAuthenticationError,
    onDismissBanner,
  };
}

export { usePagesProvider };
